<template>
  <NuxtLayout name="pdp" :page-name="store.product?.ProductName">
    <div class="lg:grid w-full grid-cols-2 gap-24 mt-20">
      <div>
        <!--Desktop-->
        <PdpImages class="hidden lg:flex lg:mb-24" />

        <!-- Both -->
        <div class="flex flex-col gap-8 md:gap-16">
          <PdpProductTitleSection />
          <PdpLabelList />
        </div>

        <!--Mobile-->
        <div class="flex flex-col gap-24 mt-24 lg:hidden">
          <PdpImages />
          <PdpUspList />
          <PdpColorSelection />
          <PdpStorageSelection />
          <PdpConditionSelection />
          <PdpPartialPaymentSelection />
          <PdpFamilySelection />
          <PdpPlansSelection />
          <PdpSpecificationImageRow />
          <PdpLoanWarning />
        </div>

        <!--Desktop-->
        <div class="hidden lg:flex lg:flex-col lg:gap-24 lg:mt-24">
          <PdpUspList />
          <div><PdpMainInfo /></div>
        </div>

        <!-- Both -->
        <div
          v-if="
            store.currentBundlePrice?.Discounts?.find(
              (discount: any) => discount.Description.length > 0,
            ) !== undefined
          "
          class="flex flex-col gap-24 mb-24"
        >
          <PdpDiscounts />
        </div>
        <PdpInfoAccordions />
      </div>
      <div>
        <!--Desktop-->
        <div class="hidden lg:flex lg:flex-col lg:gap-24">
          <PdpColorSelection />
          <PdpStorageSelection />
          <PdpConditionSelection />
          <PdpPartialPaymentSelection />
          <PdpFamilySelection />
          <PdpPlansSelection />
          <PdpSpecificationImageRow />
          <PdpLoanWarning />
        </div>
      </div>
    </div>

    <PdpPriceFooter />
    <section class="mt-48 md:mt-100 pb-160">
      <PdpFaq />
    </section>
    <FamilyDrawer />
  </NuxtLayout>
</template>

<script lang="ts" setup>
import FamilyDrawer from '~/components/family/Drawer.vue';
import { usePdpSEOMetaOgData } from '~/composables/pdp/seoInput';
import { usePdpSEOMetaStructuredData } from '~/composables/pdp/usePdpSEOMetaStructuredData';
import { usePdpListenToViewItem } from '~/composables/tracking/usePdpListenToViewItem';
import { general_setPageTheme } from '~/utils/general/pageThemeClassFromMarkup';
import { family_addFamilyPlanDataToPdp } from '~/utils/family/addFamilyPlanDataToPdp';

const route = useRoute();
const store = useMyProductPageStore();
const familyStore = useFamilyStore();
const { $api } = useNuxtApp();
const notifications = useNotificationStore();
notifications.reset();

definePageMeta({
  layout: false,
});

usePdpListenToViewItem({
  listenToColor: true,
  listenToDownpayTime: true,
  listenToPlanId: true,
  listenToStorage: true,
});

const { data, error } = await useAsyncData(
  route.fullPath,
  async () => {
    const productData = await $api.cellPhonePage.getCellPhoneByProductLinkUrl(
      route.path,
    );
    return productData;
  },
  {
    transform: cache_addFetchedAtTransform,
    getCachedData: cache_getCachedDataFromServer,
  },
);

if (error.value) {
  if (error.value.statusCode === 404) {
    const pathSegments = route.fullPath.split('/');
    const path = pathSegments.slice(0, -1).join('/');
    await navigateTo(path, { redirectCode: 302, external: true });
  }
  console.error(error.value);
  notifications.setStandardErrorNotification();
}

if (data && data.value) {
  store.populatePACState(data.value, route.query);
}

usePdpSEOMetaOgData({ productCategory: 'phone' });
usePdpSEOMetaStructuredData();

if (store.product?.ShowOnHomePage === false) {
  useHead({
    meta: [{ name: 'robots', content: 'noindex, nofollow' }],
  });
}

const currentProductVariantWatchable = computed(() => store.currentVariant);
const currentBindingPeriodWatchable = computed(
  () => store.selectedBindingPeriod,
);
const downPaymtimeCopy = computed(
  () => store.productState.selectedDownPaymentTime,
);

familyStore.initializeDrawerOnPDP();
general_setPageTheme(data.value?.Description);

watch(downPaymtimeCopy, async (downPayTime, prevDownPayTime) => {
  if (
    store.isFamilyPlanSet &&
    ((prevDownPayTime === '1' && downPayTime !== '1') ||
      (downPayTime === '1' && prevDownPayTime !== '1'))
  ) {
    await family_addFamilyPlanDataToPdp();
  }
});

watch(currentProductVariantWatchable, (value) => {
  if (value) {
    familyStore.initializeDrawerOnPDP();
  }
});

watch(currentBindingPeriodWatchable, (value, prevValue) => {
  if (value !== prevValue) {
    familyStore.initializeDrawerOnPDP();
  }
});

watch(
  currentProductVariantWatchable,
  (variant) => {
    if (variant?.DisableBuyButton === true) {
      notifications.setNewNotification({
        type: 'error',
        title: 'Slutsåld',
        msg: 'Den här produktvarianten är antingen slutsåld eller otillgänglig just nu. Testa övriga kombinationer!',
        isCloseable: true,
        link: {
          url: 'https://www.halebop.se/support',
          text: 'Kontakta supporten',
        },
      });
    } else {
      if (notifications.title === 'Slutsåld') {
        notifications.reset();
      }
    }
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped></style>
